// store/modules/competition.js
import {secureStorage} from "@/services/secureStorage";

// Helpers para mantener datos previos cuando llegan payloads parciales o vacíos
const SUMMARY_KEYS = ['approved', 'pending', 'denied', 'waiting', 'paid', 'paied', 'min_approved', 'athlete_count'];
const hasSummaryMetrics = (summary) => summary && typeof summary === 'object' && SUMMARY_KEYS.some(key => Number.isFinite(Number(summary[key])));
const selectSummary = (incoming, previous) => {
    if (hasSummaryMetrics(incoming)) return incoming;
    if (hasSummaryMetrics(previous)) return previous;
    return incoming || previous || {};
};
const selectAthletesMin = (incoming, previous) => {
    const incNum = Number(incoming);
    const prevNum = Number(previous);
    const incValid = Number.isFinite(incNum) && incNum > 0;
    const prevValid = Number.isFinite(prevNum) && prevNum > 0;
    if (incValid) return incNum;
    if (prevValid) return prevNum;
    if (Number.isFinite(incNum)) return incNum;
    return Number.isFinite(prevNum) ? prevNum : null;
};
const buildCompetitionPayload = (data, previous) => {
    if (Array.isArray(data)) return data;

    let safeData = data || {};
    if (typeof safeData === 'string') {
        try {
            safeData = JSON.parse(safeData);
        } catch (e) {
            console.warn('No se pudo parsear payload de competition', e);
            safeData = {};
        }
    }

    const prevSafe = (previous && typeof previous === 'object' && !Array.isArray(previous)) ? previous : {};

    const hasRoundSizes = Object.prototype.hasOwnProperty.call(safeData, 'roundSizes')
        || Object.prototype.hasOwnProperty.call(safeData, 'round_sizes');
    const hasCalendars = Object.prototype.hasOwnProperty.call(safeData, 'stageCalendars')
        || Object.prototype.hasOwnProperty.call(safeData, 'competition_stage_calendars')
        || Object.prototype.hasOwnProperty.call(safeData, 'stage_calendars');

    const roundSizes = hasRoundSizes
        ? (safeData?.roundSizes ?? safeData?.round_sizes ?? [])
        : (prevSafe?.round_sizes ?? prevSafe?.roundSizes);

    const calendars = hasCalendars
        ? (safeData?.stageCalendars
            ?? safeData?.competition_stage_calendars
            ?? safeData?.stage_calendars
            ?? [])
        : (prevSafe?.stageCalendars
            ?? prevSafe?.competition_stage_calendars
            ?? prevSafe?.stage_calendars);

    const normalized = {
        ...prevSafe,
        ...safeData,
        ...(roundSizes !== undefined ? { roundSizes, round_sizes: roundSizes } : {}),
        ...(calendars !== undefined ? {
            stageCalendars: calendars,
            competition_stage_calendars: calendars,
            stage_calendars: calendars,
        } : {}),
    };

    return {
        ...normalized,
        athletes_summary: selectSummary(safeData?.athletes_summary, prevSafe?.athletes_summary),
        athletes_min: selectAthletesMin(safeData?.athletes_min, prevSafe?.athletes_min),
        athletes_ordered_at: safeData?.athletes_ordered_at ?? prevSafe?.athletes_ordered_at ?? null,
        athletes_ordered_by: safeData?.athletes_ordered_by ?? prevSafe?.athletes_ordered_by ?? null,
    };
};

export default {
    namespaced: true,
    state: () => ({
        competitionIdToView: JSON.parse(secureStorage.getItem('competitionIdToView')) || null,
        competitionIdToEdit: JSON.parse(secureStorage.getItem('competitionIdToEdit')) || null,
        competitionData: JSON.parse(secureStorage.getItem('competitionData')) || [],
        appNavigation: JSON.parse(secureStorage.getItem('appNavigation')) || {}
    }),
    mutations: {
        setCompetitionData(state, data) {
            state.competitionData = data;
            secureStorage.setItem('competitionData', JSON.stringify(data)); // Guardar en sesión
        },
        setCompetitionIdToView(state, id) {
            state.competitionIdToView = id;
            secureStorage.setItem('competitionIdToView', JSON.stringify(id)); // Almacena el competitionIdToView en secureStorage
        },
        setCompetitionIdToEdit(state, id) {
            state.competitionIdToEdit = id;
            secureStorage.setItem('competitionIdToEdit', JSON.stringify(id)); // Asegura que se almacene como string
        },
        addOrUpdateCompetition(state, data) {
            state.competitionData = buildCompetitionPayload(data, state.competitionData);
            secureStorage.setItem('competitionData', JSON.stringify(state.competitionData));
        },
        // 👇 Acceso y actualización de navegación por zona
        updateAppNavigationZone(state, { zone, data }) {
            state.appNavigation = {
                ...state.appNavigation,
                [zone]: {
                    ...(state.appNavigation[zone] || {}),
                    ...data
                }
            };
            secureStorage.setItem('appNavigation', JSON.stringify(state.appNavigation));
        },
        clearAppNavigationZone(state, zone) {
            if (state.appNavigation[zone]) {
                delete state.appNavigation[zone];
                secureStorage.setItem('appNavigation', JSON.stringify(state.appNavigation));
            }
        },
        resetCompetitionData(state) {
            state.competitionData = null;
            secureStorage.removeItem('competitionData');
        },
        resetCompetitionIdToView(state) {
            state.competitionIdToView = null;
            secureStorage.removeItem('competitionIdToView');
        },
        resetCompetitionIdToEdit(state) {
            state.competitionIdToEdit = null;
            secureStorage.removeItem('competitionIdToEdit');
        },
    },
    actions: {
        setCompetitionIdToEdit({ commit }, id) {
            commit('setCompetitionIdToEdit', id);
        },
        setCompetitionIdToView({ commit }, id) {
            commit('setCompetitionIdToView', id);
        },
        setCompetition({ commit, state }, data) {
            const merged = buildCompetitionPayload(data, state.competitionData);
            commit('setCompetitionData', merged);
        },
        upsertCompetition({ commit }, data) {
            commit('addOrUpdateCompetition', data);
        },
        updateAppNavigationZone({ commit }, payload) {
            commit('updateAppNavigationZone', payload);
        },
        clearAppNavigationZone(context, zone) {
            context.commit('clearAppNavigationZone', zone);
        },
        clearCompetition({ commit }) {
            commit('resetCompetitionData');
        },
        clearCompetitionIdToEdit({ commit }) {
            commit("resetCompetitionIdToEdit");
        },
        clearCompetitionIdToView({ commit }) {
            commit("resetCompetitionIdToView");
        },

    },
    getters: {
        getCompetitionId: state => state.competitionIdToEdit,
        getCompetitionIdView: state => state.competitionIdToView,
        getCompetitionData: state => state.competitionData,
        getNavigationZone: state => zone => state.appNavigation[zone] || {}
    }
};
